import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Action } from '../../../models/core/action';
import { ExportService } from '../../../services/export/export.service';
import { ClaimExportMapper } from '../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ExportFormat } from '../../core/buttons/export-button.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { ClaimLabelComponent } from '../../core/labels/claim-label.component';
import { TagComponent, TagType } from '../../core/labels/tag.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { ExpandPanelComponent } from '../../core/panels/expand-panel.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { getExpandSubject } from '../../../functions/get-expand-subject';

@Component({
  selector: 'app-task-table',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    ActionRowComponent,
    ButtonComponent,
    CommonModule,
    ActionButtonComponent,
    MenuComponent,
    TagComponent,
    ReactiveFormsModule,
    ExpandPanelComponent,
    ClaimLabelComponent,
    RouterModule,
  ],
  template: `
    @if (isPageTodo) {
      <app-action-row useMobileLayout>
        <app-button left1 (click)="newTask()" label="New Task" />
      </app-action-row>
    } @else {
      <app-action-row useMobileLayout>
        <app-button left1 (click)="newClaimTask()" label="New Task" />
      </app-action-row>
    }

    <app-table
      [dataSource]="dataSource"
      [loading]="loading"
      [error]="error"
      [loadingHeight]="280"
      loadingStyle="Box"
      [pageSize]="10"
      [showSelection]="showSelection"
      [showExport]="true"
      [scrollable]="false"
      (configure)="showPickList = true"
      (selectedEvent)="onSelection($event)"
      [expandable]="true"
      [recordLabel]="'Task'"
      [showFilter]="true"
      [showFilterButton]="true"
      (export)="onExport($event)"
      [globalFilterFields]="[
        'status',
        'fileNumber',
        'firstName',
        'lastName',
        'title',
      ]"
      #table
    >
      <app-column name="status" label="Status">
        <ng-template let-data>
          <app-tag [type]="getTagStyle(data.status)">{{ data.status }}</app-tag>
        </ng-template>
      </app-column>
      <app-column name="fileNumber" label="Claim">
        <ng-template let-data>
          <app-claim-label
            [claim]="data"
            fileNumber="{{ data.fileNumber }}"
            [showWatchButton]="!!data.fileNumber"
            [recordSource]="data.recordSource"
            [recordId]="data.navigatingRecID"
            [fileNumFormat]="data.format"
          ></app-claim-label>
        </ng-template>
      </app-column>
      <app-column name="lastName" label="Name">
        <ng-template let-data>
          {{ data.firstName }} {{ data.lastName }}
        </ng-template>
      </app-column>
      <app-column name="title" label="Title" />
      <app-column name="dueDate" label="Due Date" format="d" />
      <app-column name="actions" label="Actions" [sort]="false">
        <ng-template let-data>
          <app-action-button
            [menu]="menu"
            [row]="data"
            (click)="menu.toggle($event, data)"
          />
        </ng-template>
      </app-column>

      <app-column
        name="message"
        label="Message"
        [fullRow]="true"
        [fullRowLabel]="false"
      >
        <ng-template let-data>
          <app-expand-panel
            [expanded]="table.expanded"
            [label]="getExpandSubject(data.title, data.dueDate) || 'Task'"
            collapseHeight="80px"
          >
            {{ data.message }}
          </app-expand-panel>
        </ng-template>
      </app-column>
    </app-table>
    <app-menu [items]="actions" #menu (click)="onMenu($event, menu.data)" />
  `,
  styles: ``,
})
export class TaskTableComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  private exportService = inject(ExportService);
  private datePipe = inject(DatePipe);

  protected getExpandSubject = getExpandSubject;

  @Output() searchCountEvent = new EventEmitter<number>();
  @Output() markTaskComplete = new EventEmitter<boolean>();

  @Input()
  showDateAccessed = false;

  @Input()
  showSelection = false;

  @Input()
  headerText: string;

  @Input() dataSource: any[] = [];

  @Input()
  isPageTodo = false;

  @Input()
  claim: any = null;

  @Input()
  loading = false;

  @Input()
  error: any = null;

  showPickList = false;
  watchlist = new Set<any>();
  loaded = false;

  disableMarkComplete = true;

  actions: Action[] = [
    { label: 'Mark complete', key: 'complete' },
    { label: 'Edit', key: 'edit' },
  ];

  ngOnInit() {
    if (!this.isPageTodo) {
      this.claim = this.view.currentClaim();
    }
  }

  ngOnChanges(changes: any) {
    if (changes.dataSource) {
      this.searchCountEvent.emit(this.dataSource.length);
    }
  }

  getTagStyle(status: string): TagType {
    switch (status) {
      case 'Open':
        return 'Info';
      case 'Past due':
        return 'Error';
      case 'Draft':
        return 'Warn';
      case 'Completed':
        return 'Success';
      default:
        return 'Success';
    }
  }

  getBackData() {
    return {
      backRouterLink: document.location.pathname,
      backLabel: 'My to-do',
    };
  }

  newTask() {
    this.storage.set('current-task', this.getBackData());
    this.router.navigate(['/task-edit']);
  }

  newClaimTask() {
    const data = {
      ...this.getBackData(),
      isClaimSpecific: true,
      claimUID: this.claim?.recordId,
      contractNumber: this.claim?.contractNumber,
      employeeUID: this.claim?.hrMasterUid
        ? Number(this.claim.hrMasterUid)
        : null,
    };
    this.storage.set('current-task', data);
    this.router.navigate(['/task-edit']);
  }

  onMenu(event: any, data: any) {
    if (event.key === 'edit') {
      data.backRouterLink = document.location.pathname;
      data.backLabel = 'My to-do';

      if (!this.isPageTodo && this.claim) {
        data.isClaimSpecific = true;
        data.claimUID = this.claim.recordId;
        data.recordSource = this.claim.recordSource;
        data.contractNumber = this.claim.contractNumber;
        data.employeeUID = this.claim.employeeId;
      }

      this.storage.set('current-task', data);
      this.router.navigate(['/task-edit']);
    } else if (event.key === 'complete') {
      this.storage.set('current-task', data);
      this.markTaskComplete.emit(true);
    }
  }

  onSelection(selections: any[]) {
    this.disableMarkComplete = selections.length === 0;
  }

  async onExport(format: ExportFormat) {
    try {
      let columns = [
        { propertyName: 'status', headerText: 'Status' },
        { propertyName: 'fileNumber', headerText: 'Claim' },
        { propertyName: 'firstName', headerText: 'First Name' },
        { propertyName: 'lastName', headerText: 'Last Name' },
        { propertyName: 'title', headerText: 'Title' },
        { propertyName: 'dueDate', headerText: 'Due Date', format: 'MM/dd/yyyy' },
        { propertyName: 'message', headerText: 'Message' },
      ];
      const excludedProperties = new Set();
      if (!(this as any).showTitle) excludedProperties.add('title');
      columns = columns.filter(
        (col) => !excludedProperties.has(col.propertyName),
      );
      const rows = this.dataSource;
      const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
      const filename = `Tasks_${formattedDate}.${format}`;
      if (format === 'csv') {
        const csvData = ClaimExportMapper.toCsvExport(rows, columns, 'Tasks');
        await this.exportService.exportCsv(csvData, filename);
      } else if (format === 'xlsx') {
        const excelData = ClaimExportMapper.toExcelExport(
          rows,
          columns,
          'Tasks',
          'OmniInsights Export',
        );
        await this.exportService.exportExcel(excelData, filename);
      }
    } catch (err) {
      if ((this as any).handleError) {
        (this as any).handleError(err);
      } else {
        (this as any).error = err;
      }
    }
  }
}
